/**
 * @file quantity.ts
 * @module app/modules/orders-delivery/utils/quantity
 * @description Shared order quantity display helper for the Orders Delivery module.
 * Returns a span node with a consistent label and title for total or processed quantity.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.0
 * @version 2.1.2
 */

import React from 'react';
import { OrderQuantityLike } from '../types/order-metrics';
import {
  normalizeOrderMetric,
  subtractProcessedMetric,
  sumOrderMetricValues,
} from './order-metric-values';

/**
 * Sums line stock counts; returns null when a supplied value is not finite.
 * @param {OrderQuantityLike} orderVal - The order quantity object containing processed and original ordered quantities.
 * @returns {number | null} The total quantity, or null when unavailable.
 */
export const getTotalQuantityByCalculatingStockCount = (
  orderVal: OrderQuantityLike
) => {
  const quantities = orderVal?.totalQuantities;
  if (quantities != null && !Array.isArray(quantities)) {
    return null;
  }
  return sumOrderMetricValues(
    (quantities ?? []).map((item) => item?.stockCount),
  );
};

export const getRemainingOrderQuantity = (orderVal: OrderQuantityLike) =>
  subtractProcessedMetric(
    getTotalQuantityByCalculatingStockCount(orderVal),
    orderVal?.mProcessedQuantities,
  );

export const isPartiallyProcessedOrder = (orderVal: OrderQuantityLike) => {
  const total = getTotalQuantityByCalculatingStockCount(orderVal);
  const processed = normalizeOrderMetric(orderVal?.mProcessedQuantities);
  return total !== null && processed !== null && processed > 0 && total > processed;
};

const createOrderQuantitySpan = (
  className: string | undefined,
  title: string | undefined,
  children: string | number
) => {
  return React.createElement(
    'span',
    {
      className: `${className ?? ''} order-qty`,
      title,
    },
    children
  );
};

/**
 * Returns a span node using the original total-items logic by summing stockCount values.
 * @param {OrderQuantityLike} orderVal - The order quantity object containing total quantities.
 * @param {string} [className] - Optional additional class name for the span element.
 * @returns {React.ReactElement} A React span element displaying the summed item count.
 */
export const getOrderItemsByCalculatingStockCountDisplay = (
  orderVal: OrderQuantityLike,
  className?: string
) => {
  const totalQuantity = getTotalQuantityByCalculatingStockCount(orderVal);

  return createOrderQuantitySpan(
    className,
    totalQuantity === null ? 'Order quantity unavailable' : `total order ${totalQuantity} quantity`,
    totalQuantity ?? '-'
  );
};